import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const refreshSession = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      setSession(data.session);
      setUser(data.session.user);
      // Invalidate seller-related queries to pick up any changes from other tabs
      queryClient.invalidateQueries({ queryKey: ["seller"] });
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
    }
  }, [queryClient]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      
      // Invalidate queries on auth state change to ensure fresh data
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["seller"] });
          queryClient.invalidateQueries({ queryKey: ["profiles"] });
        }, 0);
      }

      // Welcome email: fire once per account. The edge function dedupes via
      // profiles.welcome_email_sent_at, so repeated sign-ins are no-ops.
      if (event === 'SIGNED_IN' && newSession) {
        setTimeout(() => {
          const lang = localStorage.getItem('i18nextLng') === 'gr' ? 'gr' : 'en';
          supabase
            .from('profiles')
            .update({ preferred_language: lang })
            .eq('id', newSession.user.id)
            .then(() => supabase.functions.invoke('send-welcome-email'))
            .then(({ error }) => {
              if (error) console.warn('[AuthContext] welcome email skipped:', error.message);
            });
        }, 0);
      }
    });

    // Race getSession() against a timeout so a stalled auth call
    // doesn't leave protected routes stuck on "Loading..." forever.
    const SESSION_TIMEOUT_MS = 8000;
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      console.warn("[AuthContext] getSession() timed out after", SESSION_TIMEOUT_MS, "ms");
      setLoading(false);
    }, SESSION_TIMEOUT_MS);

    supabase.auth.getSession()
      .then(({ data }) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        setSession(data.session);
        setUser(data.session?.user ?? null);
        setLoading(false);
      })
      .catch((err) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        console.error("[AuthContext] getSession() failed:", err);
        setLoading(false);
      });


    return () => subscription.unsubscribe();
  }, [queryClient]);

  // Listen for window focus to sync session from other tabs
  useEffect(() => {
    const handleFocus = () => {
      refreshSession();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshSession]);

  // Listen for storage events (cross-tab session changes)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key?.includes('supabase.auth.token')) {
        refreshSession();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refreshSession]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
